import { getLogger } from './logger';
import type { WidgetElements, WidgetTranslations } from './types';
import { processSvgTemplate } from './ui-helpers';

const logger = getLogger('UI');

/**
 * Create widget DOM elements with SVG template
 */
export function createWidgetElements(translations: WidgetTranslations): WidgetElements {
  // Create main container widget
  const widget = document.createElement('div');

  // Process SVG template with translations
  const svgContent = processSvgTemplate(translations);

  // Create a temporary container to parse SVG
  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = svgContent;

  // Get the SVG element
  const svgElement = tempContainer.querySelector('svg');
  if (!svgElement) {
    throw new Error('SVG template could not be parsed');
  }

  // Create supporting elements for backward compatibility
  const header = document.createElement('div');
  const headerIcon = document.createElement('div');
  const content = document.createElement('div');
  const headerTitle = document.createElement('span');
  const indicator = document.createElement('div');
  const message = document.createElement('span');
  const icon = document.createElement('div');

  // Main widget container styles - responsive sizing - MADE LARGER
  // Calculate height from aspect ratio (1462 / 751) for better browser compatibility
  const widgetWidth = 900;
  const widgetHeight = Math.round(widgetWidth * (751 / 1462));

  Object.assign(widget.style, {
    position: 'fixed',
    width: `${widgetWidth}px`,
    height: `${widgetHeight}px`,
    maxWidth: '95vw',
    maxHeight: `${(95 * 751) / 1462}vw`, // Maintain aspect ratio responsively
    zIndex: '9999',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif',
    fontSize: '18px',
    color: '#333333',
    overflow: 'hidden',
    contain: 'layout style paint',
    transform: 'translateZ(0)',
    willChange: 'transform',
    display: 'block',
    boxSizing: 'border-box',
  });

  // SVG styling to fit container
  Object.assign(svgElement.style, {
    width: '100%',
    height: '100%',
    display: 'block',
  });

  // Setup stop button with enhanced event handling
  const stopButton = svgElement.querySelector('#stop-button');
  if (stopButton) {
    const stopButtonElement = stopButton as HTMLElement;

    // Set cursor and comprehensive accessibility attributes
    stopButtonElement.style.cursor = 'pointer';
    stopButtonElement.setAttribute('role', 'button');
    stopButtonElement.setAttribute('tabindex', '0');

    // Add data attribute for event handling identification
    stopButtonElement.setAttribute('data-sirmaai-stop-button', 'true');

    logger.debug('Stop button found and configured', {
      hasStopButton: true,
      tagName: stopButtonElement.tagName,
      attributes: {
        role: stopButtonElement.getAttribute('role'),
        tabindex: stopButtonElement.getAttribute('tabindex'),
      },
    });
  } else {
    logger.warn('Stop button not found in SVG template');
  }

  // Build DOM structure
  widget.appendChild(svgElement);

  // Hide the legacy elements but keep them for backward compatibility
  header.style.display = 'none';
  content.style.display = 'none';
  headerIcon.style.display = 'none';
  icon.style.display = 'none';
  indicator.style.display = 'none';

  // Append hidden elements for compatibility
  // widget.appendChild(header);
  // widget.appendChild(content);
  // widget.appendChild(headerIcon);
  // widget.appendChild(icon);
  // widget.appendChild(indicator);
  // widget.appendChild(message);
  // widget.appendChild(headerTitle);

  return {
    widget,
    header,
    headerIcon,
    headerTitle,
    content,
    indicator,
    message,
    icon,
    svgElement,
    stopButton: stopButton as HTMLElement | undefined,
  };
}
